/**
 * SurfaceStage — `section.hero`, the third section of the spine.
 *
 * Built to `publisher-drop-hub.html` lines 1124-1172.
 *
 * ## It is a STAGE, not a hero, and that distinction is the design
 *
 * The anchor's own words: it holds whatever the surface's most valuable
 * content is right now, and it SIZES TO THAT CONTENT. Three states, and
 * nothing else on the page moves between them:
 *
 *   · IDENTITY  — nothing playing. The stage IS the title: lockup, phase, the
 *                 CTAs, the counters. `--band`, matching the rails either side.
 *   · WATCHING  — something was selected from any rail. The stage becomes the
 *                 viewer and identity compresses to the bar beneath it, so the
 *                 surface never stops saying what it is.
 *   · EXPANDED  — the stage takes the 60px the column gives up (`--surface-band-expanded`)
 *                 and the column becomes chat. It does NOT swallow the column:
 *                 the viewer most likely to want to talk is the one most likely
 *                 to leave for Twitch to do it.
 *
 * The left rail, the right column and every strip below are identical in all
 * three. That is the point — the stage is a slot whose occupant decides its
 * size, not a different page.
 *
 * ## Why identity is not simply hidden when watching
 *
 * `.hero__vm` re-states the lockup, the creator and the tags under the video.
 * A surface that swaps its whole identity out for an embed is indistinguishable
 * from an embed. The compressed bar is what makes the stream read as something
 * happening ON this surface rather than a player someone pasted in.
 */

import React from "react";
import {safeCssUrl} from "@/widgets/play/safeHref";

/** Which of the three states the stage is in. Mirrors `StageState` in `occupancy.ts`. */
export type SurfaceStageState = "IDLE" | "WATCHING" | "EXPANDED";

/** A statistic under the CTAs — the anchor's 75 Days / 48.4% / 840 Creators trio. */
export interface SurfaceStageStat {
    id: string;
    /** Small caps above the figure. */
    label: string;
    /** Pre-formatted. This component does not decide a number's locale. */
    value: string;
    /** Small caps below the figure. Optional — not every figure needs qualifying. */
    detail?: string;
}

/** A face in the pile. A photo when the roster has one; the monogram otherwise. */
export interface SurfaceStageFace {
    id: string;
    initials: string;
    imageUrl?: string;
}

export interface SurfaceStageOccupant {
    /**
     * The actual player. When present the stage MOUNTS IT rather than showing a
     * still — this container exists to be watched in, and a still with a play
     * button that navigates away is a thumbnail, not a stage. The anchor shows
     * a still because a mock has no player to mount; product has one.
     */
    player?: React.ReactNode;
    /** Still or poster, shown until a player is handed over. */
    imageUrl?: string;
    live?: boolean;
    /** e.g. "190K watching". Pre-formatted. */
    viewers?: string;
    /** Who is playing it. */
    title: string;
    subtitle?: string;
    /** Chips on the right of the identity bar. The first takes the accent. */
    tags?: readonly string[];
    /** Where this stream's chat lives — what the EXPANDED column mounts. */
    chat?: { platform: string; channel: string };
    onPlay?: () => void;
}

export interface SurfaceStageProps {
    state: SurfaceStageState;
    /** Background art for the identity state. */
    imageUrl?: string;

    /** ── IDENTITY ── */
    /** The surface's lockup. Type is the fallback, never the default — see below. */
    logoUrl?: string;
    name: string;
    /** e.g. "Launching Oct 23". Renders with the pulsing dot. */
    phaseLabel?: string;
    synopsis?: string;
    /** Primary then secondary. More than two crowds the band. */
    actions?: React.ReactNode;
    stats?: readonly SurfaceStageStat[];
    faces?: readonly SurfaceStageFace[];
    /** Count beyond the shown faces — renders as the pile's "+N" chip. */
    facesOverflow?: number;
    /**
     * What the pile does when tapped. The faces read as a control — people try
     * them — so without this the hero advertises an action it does not have.
     */
    onFacesClick?: () => void;
    /** The line beside the pile — "Co-streaming the reveal right now". */
    facesLabel?: string;

    /** ── WATCHING / EXPANDED ── */
    occupant?: SurfaceStageOccupant;
    /**
     * The NON-STREAMING occupant — what holds the stage when nothing plays.
     *
     * Supplied, it REPLACES the identity block below rather than sitting beside
     * it: the stage holds one focused occupant, and rendering both would be two
     * things competing for the same slot.
     *
     * Absent, the identity block renders as it always has. That is the right
     * default — the publisher anchor's resting content IS its lockup, phase
     * line and counters, and a Hub that supplies nothing should keep it rather
     * than go blank.
     */
    idleOccupant?: React.ReactNode;
    /**
     * Dismiss the occupant — back to the idle stage. Rendered as an × at the
     * meta bar's right edge. The anchor has no such control because its stage
     * states are demo radios; a real viewer needs the way back.
     */
    onCloseOccupant?: () => void;
}

export const SurfaceStage: React.FC<SurfaceStageProps> = ({
    state, imageUrl, logoUrl, name, phaseLabel, synopsis, actions, stats = [],
    faces = [], facesOverflow, facesLabel, onFacesClick, occupant, idleOccupant, onCloseOccupant,
}) => {
    const watching = state !== "IDLE" && Boolean(occupant);

    return (
        <section className="sh-hero">
            {imageUrl && <span className="sh-hero__img" style={{backgroundImage: safeCssUrl(imageUrl)}} />}
            <span className="sh-hero__sc" />

            {watching && occupant &&
                <div className="sh-hero__play">
                    <div
                        className="sh-hero__v"
                        style={!occupant.player && occupant.imageUrl
                            ? {backgroundImage: safeCssUrl(occupant.imageUrl)}
                            : undefined}
                    >
                        {occupant.player}
                        {/*
                          * The scrim, the badges and the play control belong to
                          * the STILL. Over a mounted player they sit on top of
                          * the video and its own controls, so a viewer clicks
                          * our overlay instead of the platform's scrubber.
                          */}
                        {!occupant.player &&
                            <>
                                <span className="sh-hero__vsc" />
                                {occupant.live && <span className="sh-hero__vlv">● Live</span>}
                                {occupant.viewers && <span className="sh-hero__vx">{occupant.viewers}</span>}
                                <span className="sh-hero__vb">
                                    {/*
                                      * A `<button>` inside the anchor's `<u>` slot.
                                      * The anchor uses a bare `<u>` because a mock
                                      * needs no keyboard; a play control that only
                                      * a mouse can reach is unusable for anyone
                                      * navigating by tab. The spine styles `u`, so
                                      * it stays as the visual shell.
                                      */}
                                    <u>
                                        <button
                                            type="button"
                                            onClick={occupant.onPlay}
                                            aria-label={`Play ${occupant.title}`}
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </button>
                                    </u>
                                </span>
                            </>}
                    </div>
                    {/*
                      * Identity, compressed. The surface never stops saying what
                      * it is — see the header comment.
                      */}
                    <div className="sh-hero__vm">
                        {logoUrl && <img className="sh-hero__vlock" src={logoUrl} alt={name} />}
                        {logoUrl && <span className="sh-hero__vsep" />}
                        <span className="sh-hero__vwho">
                            <b>{occupant.title}</b>
                            {occupant.subtitle && <span>{occupant.subtitle}</span>}
                        </span>
                        {onCloseOccupant &&
                            <button
                                type="button"
                                className="sh-hero__vcl"
                                onClick={onCloseOccupant}
                                aria-label="Close player"
                            >
                                ×
                            </button>}
                        {occupant.tags && occupant.tags.length > 0 &&
                            <span className="sh-hero__vtags">
                                {occupant.tags.map((tag, i) =>
                                    /*
                                     * The first tag takes the accent because it is
                                     * the campaign's own mark (#MW4Reveal); the rest
                                     * are qualifiers. Two accented chips side by side
                                     * make neither of them read as the important one.
                                     */
                                    <span className={`sh-tg${i === 0 ? " sh-tg--o" : ""}`} key={tag}>{tag}</span>)}
                            </span>}
                    </div>
                </div>}

            {/*
              * A supplied non-streaming occupant REPLACES the identity block.
              * One focused occupant per stage — rendering both would put two
              * things in the same slot.
              */}
            {!watching && idleOccupant &&
                <div className="sh-hero__c sh-hero__idle">{idleOccupant}</div>}

            {!watching && !idleOccupant &&
                <div className="sh-hero__c sh-hero__idle">
                    {phaseLabel &&
                        <span className="sh-hero__lv"><span className="sh-pulse" />{phaseLabel}</span>}

                    {/*
                      * CREST + WORDMARK, one lockup (the anchor's `hero__id`).
                      * The mark stays small beside the name set in display
                      * type; with no crest the type stands alone. Replaced a
                      * logo-only lockup that painted the brand mark at title
                      * scale and never said the campaign's name.
                      */}
                    <span className="sh-hero__id">
                        {logoUrl && <img className="sh-hero__crest" src={logoUrl} alt="" />}
                        <span className="sh-hero__wm">{name}</span>
                    </span>

                    {synopsis && <p className="sh-hero__s">{synopsis}</p>}
                    {actions && <div className="sh-hero__b">{actions}</div>}

                    {stats.length > 0 &&
                        <div className="sh-hero__st">
                            {stats.map(stat =>
                                <span key={stat.id}>
                                    <span>{stat.label}</span>
                                    <b>{stat.value}</b>
                                    {stat.detail && <em>{stat.detail}</em>}
                                </span>)}
                        </div>}

                    {faces.length > 0 &&
                        React.createElement(
                            onFacesClick ? "button" : "div",
                            onFacesClick
                                ? {
                                    className: "sh-pile sh-pile--go",
                                    type: "button",
                                    onClick: onFacesClick,
                                    "aria-label": facesLabel ? `Go to ${facesLabel}` : "Go to the roster",
                                }
                                : {className: "sh-pile"},
                            <>
                                {faces.map(face =>
                                    <i key={face.id}>
                                        {face.imageUrl
                                            ? <img src={face.imageUrl} alt="" />
                                            : face.initials}
                                    </i>)}
                                {facesOverflow !== undefined && facesOverflow > 0 &&
                                    <i className="sh-n">+{facesOverflow}</i>}
                                {facesLabel && <b>{facesLabel}</b>}
                            </>,
                        )}
                </div>}
        </section>
    );
};

export default SurfaceStage;
